import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { createSelector } from 'reselect';

const getApplicationState = (state) => state.applicationState;
const getTemplateData = (state) => state.applicationState?.templateData;
const getColors = (state) => state.applicationState?.templateData?.colors;
const getGeo = (state) => state.applicationState?.geo;
const getNavigation = (state) => state.applicationState?.navigation;
const getFooter = (state) => state.applicationState?.templateData?.footer;
const getCities = (state) => state.applicationState?.geo?.cities;
const getLocalities = (state) => state.applicationState?.geo?.localities;
const getPrincipalOffice = (state) => state?.applicationState?.templateData?.principalOffice;

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

export const selectApplicationState = createSelector(
  [getApplicationState],
  (appState) => appState || EMPTY_OBJECT
);

export const selectGeoDataIsReady = createSelector(
  [getCities, getLocalities],
  (cities, localities) => cities?.isReady && localities?.isReady
);

export const selectCities = createSelector([getCities], (cities) => cities?.data || EMPTY_ARRAY);

export const selectLocalities = createSelector(
  [getLocalities],
  (localities) => localities?.data || EMPTY_ARRAY
);

export const selectColors = createSelector([getColors], (colors) => {
  if (!colors || colors.length === 0) return null;
  try {
    return JSON.parse(colors[0].valor);
  } catch (error) {
    console.warn('Error parsing colors:', error);
    return null;
  }
});

export const selectHeader = createSelector(
  [getTemplateData],
  (templateData) => templateData?.header || EMPTY_OBJECT
);

export const selectNavigation = createSelector(
  [getNavigation],
  (navigation) => navigation || EMPTY_OBJECT
);

export const selectFooter = createSelector([getFooter], (footer) => footer || EMPTY_OBJECT);

export const selectFooterDetalle = createSelector(
  [getFooter],
  (footer) => footer?.detalle || EMPTY_ARRAY
);

export const selectFooterLegalLinks = createSelector(
  [getFooter],
  (footer) => footer?.legalLinks || EMPTY_ARRAY
);

export const selectFooterUtilLinks = createSelector(
  [getFooter],
  (footer) => footer?.utilLink || EMPTY_ARRAY
);

export const selectTemplateData = createSelector(
  [getTemplateData],
  (templateData) => templateData || EMPTY_OBJECT
);

export const selectPrincipalOffice = createSelector(
  [getPrincipalOffice],
  (principalOffice) => principalOffice || EMPTY_OBJECT
);

export const selectTemplateImage = createSelector(
  [getApplicationState],
  (appState) => appState?.templateImage || ''
);

const normalizeHeaderImageText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getHeaderImageValue = (item) => String(item?.valor || '').trim();

const hasHeaderImageValue = (item) => Boolean(getHeaderImageValue(item));

const isResponsiveHeaderImageItem = (item) => {
  const searchableText = [
    item?.tipoEnlace?.nombre,
    item?.tipoEnlace?.descripcion,
    item?.categoria?.nombre,
    item?.categoria?.descripcion,
    item?.nombre,
    item?.descripcion,
    item?.label,
  ]
    .map(normalizeHeaderImageText)
    .join(' ');

  return searchableText.includes('responsive');
};

const selectHeaderImageValue = (templateData, predicate) => {
  const item = templateData?.headerImagen?.find(
    (headerImageItem) =>
      hasHeaderImageValue(headerImageItem) && predicate(headerImageItem)
  );

  return getHeaderImageValue(item) || null;
};

export const selectHeaderImagen = createSelector(
  [getTemplateData],
  (templateData) =>
    selectHeaderImageValue(
      templateData,
      (headerImageItem) => !isResponsiveHeaderImageItem(headerImageItem)
    )
);

export const selectHeaderResponsiveImagen = createSelector(
  [getTemplateData],
  (templateData) => selectHeaderImageValue(templateData, isResponsiveHeaderImageItem)
);

export const selectBannersState = createSelector(
  [(state) => state?.bannersState],
  (bannersState) => bannersState || EMPTY_OBJECT
);

export const selectSectionState = createSelector(
  [(state) => state?.sectionState],
  (sectionState) => sectionState || EMPTY_OBJECT
);

export const selectToken = createSelector(
  [getApplicationState],
  (appState) => appState?.token || ''
);

export const selectPaisId = createSelector(
  [selectApplicationState],
  (appState) => {
    const raw = appState?.paisId ?? 1;
    return Number(raw) || 1;
  }
);

export const selectGeoData = createSelector([getGeo], (geo) => geo || null);

export const selectGeoLocalities = createSelector([getGeo], (geo) => geo?.localities || null);

export const selectGeoCities = createSelector([getGeo], (geo) => geo?.cities || null);

export const selectApplicationId = createSelector(
  [getApplicationState],
  (appState) => appState?.applicationId || 0
);

export const selectPackagesDetails = createSelector(
  [(state) => state?.packages?.details?.data],
  (details) => details || EMPTY_OBJECT
);

export const selectTemplateId = createSelector(
  [selectApplicationState],
  (appState) => appState?.templateId || null
);

export const selectTemplateInfo = createSelector(
  [selectApplicationState],
  (appState) => {
    if (!appState) return null;
    return {
      id: appState.templateId || null,
      nombre: appState.templateNombre || null,
      html: appState.templateHtml || null,
      pais_id: appState.paisId || null,
      templateImage: appState.templateImage || null,
    };
  }
);

export const selectTemplateNombre = createSelector(
  [selectApplicationState],
  (appState) => appState?.templateNombre || null
);

export const selectTemplateHtml = createSelector(
  [selectApplicationState],
  (appState) => appState?.templateHtml || null
);

export const selectEmpresaTemplateId = createSelector(
  [selectApplicationState],
  (appState) => appState?.empresaTemplateId || null
);

export const selectEmpresaTemplateActivo = createSelector(
  [selectApplicationState],
  (appState) => appState?.empresaTemplateActivo ?? null
);

export const selectEmpresaId = createSelector(
  [selectApplicationState],
  (appState) => appState?.empresaId || null
);

export const selectApplicationInfo = createSelector([selectApplicationState], (appState) => {
  if (!appState) return EMPTY_OBJECT;
  const rawPaisId = appState.paisId ?? 1;

  return {
    applicationId: appState.applicationId || '',
    nombre: appState.nombre || '',
    paisId: Number(rawPaisId) || 1,
    templateData: appState.templateData || EMPTY_OBJECT,
    token: appState.token || '',
    currency: appState.currency || EMPTY_OBJECT,
  };
});

export const selectIsPackageSearcherEnabled = createSelector(
  [selectTemplateData],
  (templateData) => templateData?.headerPaquete?.length > 0
);

export const selectIsFlightSearcherEnabled = createSelector(
  [selectTemplateData],
  (templateData) => templateData?.headerVuelos?.length > 0
);

export const selectIsHotelSearcherEnabled = createSelector(
  [selectTemplateData],
  (templateData) => templateData?.headerHoteles?.length > 0
);

export const selectWhatsAppNumber = createSelector(
  [selectTemplateData],
  (templateData) => {
    const headerWhatsApp = templateData?.header?.detalle?.find(
      (item) => item?.tipoEnlace?.descripcion === 'Whatsapp'
    );

    if (headerWhatsApp?.valor) {
      return headerWhatsApp.valor;
    }

    const footerWhatsApp = templateData?.footer?.detalle?.find(
      (item) => item?.tipoEnlace?.descripcion === 'Whatsapp'
    );

    if (footerWhatsApp?.valor) {
      return footerWhatsApp.valor;
    }

    return null;
  }
);

export const selectWhatsAppBusinessNumber = createSelector(
  [selectApplicationState],
  (appState) => appState?.whatsappBusinessNumber || null
);

export const selectFirstActiveRoute = createSelector(
  [selectIsPackageSearcherEnabled, selectIsFlightSearcherEnabled, selectIsHotelSearcherEnabled],
  (isPackageEnabled, isFlightEnabled, isHotelEnabled) => {
    if (isPackageEnabled) {
      return ROUTE_KEYS.BUSCAR_X_PAQUETES;
    }
    if (isFlightEnabled) {
      return ROUTE_KEYS.BUSCAR_X_VUELOS;
    }
    if (isHotelEnabled) {
      return ROUTE_KEYS.BUSCAR_X_HOTELES;
    }
    return ROUTE_KEYS.HOME;
  }
);

export const selectHasActiveSearchers = createSelector(
  [selectIsPackageSearcherEnabled, selectIsFlightSearcherEnabled, selectIsHotelSearcherEnabled],
  (isPackageEnabled, isFlightEnabled, isHotelEnabled) => isPackageEnabled || isFlightEnabled || isHotelEnabled
);

export default {
  geoDataIsReady: selectGeoDataIsReady,
  cities: selectCities,
  localities: selectLocalities,
};
